import { useEffect, useRef, useMemo } from 'react';
/**
 * List of available codes can be found here: https://developer.mozilla.org/en-US/docs/Web/API/UI_Events/Keyboard_event_code_values
 */
export const keyAliasMap = {
    Ctrl: ['ControlLeft', 'ControlRight'],
    Control: ['ControlLeft', 'ControlRight'],
    Shift: ['ShiftLeft', 'ShiftRight'],
    M: ['KeyM'],
    V: ['KeyV'],
};
/**
 * Normalize a KeyCombo into:
 * - keyGroups: an array of arrays, each containing one or more `KeyboardEvent.code` strings
 * - signature: a stable string key derived from sorted alias names
 */
const normalizeCombo = (input) => {
    const rawAliases = Array.isArray(input) ? input : [input];
    const sortedAliases = rawAliases.slice().sort();
    const keyGroups = sortedAliases.map((alias) => {
        return keyAliasMap[alias];
    });
    const signature = sortedAliases.join(',');
    return { keyGroups, signature };
};
/**
 * useHotkey
 * @param {KeyCombo} keys
 *   A single alias (e.g. "Escape") or an array of aliases
 *   (e.g. ["Ctrl", "Shift", "Z"]) to treat as a hotkey combination.
 * @param handler
 *   Callback function that will be called on shortcut trigger
 * @param options
 *   Hotkey options
 */
const useHotkey = (keys, handler, options = {}) => {
    const handlerRef = useRef(handler);
    const { preventDefault = false, enabled = true } = options;
    useEffect(() => {
        handlerRef.current = handler;
    }, [handler]);
    const { keyGroups, signature } = useMemo(() => normalizeCombo(keys), [keys]);
    useEffect(() => {
        const currentSet = new Set();
        let calledOnce = false;
        const downListener = (e) => {
            currentSet.add(e.code);
            const allPressed = keyGroups.every((group) => group.some((code) => currentSet.has(code)));
            if (allPressed && !calledOnce) {
                calledOnce = true;
                if (preventDefault)
                    e.preventDefault();
                handlerRef.current(e);
            }
        };
        const upListener = (e) => {
            if (keyGroups.some((group) => group.includes(e.code))) {
                currentSet.delete(e.code);
                calledOnce = false;
            }
        };
        const blurListener = () => {
            currentSet.clear();
            calledOnce = false;
        };
        if (enabled) {
            window.addEventListener('keydown', downListener);
            window.addEventListener('keyup', upListener);
            window.addEventListener('blur', blurListener);
        }
        return () => {
            window.removeEventListener('keydown', downListener);
            window.removeEventListener('keyup', upListener);
            window.removeEventListener('blur', blurListener);
        };
        // keyGroups is not provided in dependencies as it is not stable unlike signature
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, enabled, preventDefault]);
};
export default useHotkey;
